/*
  ADA / WCAG 2.2 AA — keeps the sticky header from covering a freshly keyboard-focused
  element (2.4.11 Focus Not Obscured). Measures the real header height and exposes it
  as --ada-header-offset, which ada-fixes.css applies as scroll-margin-top on focusable
  elements so the browser's built-in scroll-into-view leaves room for the header.
*/
(function () {
  'use strict';

  function getHeader() {
    return document.querySelector('sticky-header, .header-wrapper');
  }

  function updateOffset() {
    var header = getHeader();
    var height = header ? header.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--ada-header-offset', Math.ceil(height + 16) + 'px');
  }

  updateOffset();
  window.addEventListener('load', updateOffset);
  window.addEventListener('resize', updateOffset);

  var header = getHeader();
  if (header && window.ResizeObserver) {
    new ResizeObserver(updateOffset).observe(header);
  }

  /* ------------------------------------------------------------------ *
   * 2.4.11, part two: scroll the ring, not just the element.
   *
   * Dawn draws a card's focus ring on ::after of the small title link, absolutely
   * positioned against the card (component-card.css: `.card__heading a::after`, and
   * .full-unstyled-link on blog/product cards). The browser only scrolls the link
   * itself into view — on /blogs/news that link is 66px tall inside a 440px card, so
   * the ring it paints can hang well below the fold even though the link is "visible".
   * scroll-margin-top alone can't fix that: the overflow is at the bottom, and CSS has
   * no way to know how big the pseudo-element is.
   *
   * So on keyboard focus, work out the box the ring is actually painted in and nudge
   * the page the minimum amount needed to bring that whole box between the sticky
   * header and the bottom of the viewport.
   * ------------------------------------------------------------------ */
  var PAD = 16;

  function headerHeight() {
    var h = getHeader();
    if (!h) return 0;
    var r = h.getBoundingClientRect();
    // Only counts as an obstruction while it is actually pinned over the content.
    return r.top <= 0 && r.bottom > 0 ? r.bottom : 0;
  }

  // The rectangle the focus indicator is painted in — the element itself, unioned with
  // its positioning ancestor when the ring comes from an absolutely positioned ::after.
  function ringBox(el) {
    var rect = el.getBoundingClientRect();
    var top = rect.top;
    var bottom = rect.bottom;

    var after;
    try {
      after = window.getComputedStyle(el, '::after');
    } catch (e) {
      return { top: top, bottom: bottom };
    }

    if (after && after.content && after.content !== 'none' && after.position === 'absolute') {
      var host = el.offsetParent;
      if (host) {
        var hr = host.getBoundingClientRect();
        // Ignore page-level wrappers: if the positioning ancestor is nearly as tall as
        // the viewport it isn't a card, and scrolling to fit it would be worse.
        if (hr.height <= window.innerHeight * 0.9) {
          top = Math.min(top, hr.top);
          bottom = Math.max(bottom, hr.bottom);
        }
      }
    }
    return { top: top, bottom: bottom };
  }

  // Focus inside a fixed/sticky container (quick-add dialog, cart drawer, the header
  // itself) doesn't move with the page, so scrolling would only shift the content behind
  // it without bringing anything into view.
  function inFixedContainer(el) {
    for (var n = el; n && n !== document.body; n = n.parentElement) {
      var pos;
      try {
        pos = window.getComputedStyle(n).position;
      } catch (e) {
        return false;
      }
      if (pos === 'fixed' || pos === 'sticky') return true;
    }
    return false;
  }

  function isKeyboardFocus(el) {
    try {
      return el.matches(':focus-visible');
    } catch (e) {
      return true; // browsers without :focus-visible support: treat as keyboard
    }
  }

  document.addEventListener(
    'focusin',
    function (event) {
      var el = event.target;
      if (!el || typeof el.getBoundingClientRect !== 'function') return;
      if (el === document.body || el === document.documentElement) return;
      if (!isKeyboardFocus(el)) return;
      if (inFixedContainer(el)) return;

      // Let the browser finish its own scroll-into-view first, then correct it.
      window.requestAnimationFrame(function () {
        if (document.activeElement !== el) return;

        var box = ringBox(el);
        var top = headerHeight() + PAD;
        var limit = window.innerHeight - PAD;
        var delta = 0;

        if (box.bottom > limit) delta = box.bottom - limit;
        // Never push the top of the ring under the header; showing the top wins when the
        // box is too tall to fit entirely.
        if (box.top - delta < top) delta = box.top - top;

        if (Math.abs(delta) > 1) window.scrollBy(0, delta);
      });
    },
    true
  );
})();
